import { useCloseRequest } from '../use-close-request';
import { useCallback, useState } from 'react';
import type React from 'react';
import type {
  InsertSlashCommandResult,
  SlashCommand,
} from 'src/modules/shared/types/editor/slash-commands.types';
import {
  filterSlashCommands,
  insertSlashCommand,
} from 'src/modules/shared/utils/editor/slash-insert.util';

/** State for managing slash command menu visibility and interaction. */
export interface SlashState<SectionKey extends string = string> {
  /** Whether the slash command menu is open. */
  isOpen: boolean;
  /** The section key for which the menu is open. */
  sectionKey: SectionKey | null;
  /** Character offset where the slash command started. */
  startOffset: number;
  /** The current query string being filtered. */
  query: string;
  /** Index of the currently highlighted command. */
  activeIndex: number;
}

/** Configuration options for the useSlashCommands hook. */
export interface UseSlashCommandsOptions<SectionKey extends string = string> {
  /** Array of available slash commands. */
  commands: SlashCommand[];
  /** Optional section keys for filtering. */
  sectionKeys?: readonly SectionKey[];
  /** Optional function to customize insertion text for a command. */
  getInsertionText?: (command: SlashCommand) => string;
}

/** Return type for the useSlashCommands hook. */
export interface UseSlashCommandsReturn<SectionKey extends string = string> {
  /** Current state of the slash command menu, or null if closed. */
  state: SlashState<SectionKey> | null;
  /** Opens the command menu for a specific section. */
  openForSection: (sectionKey: SectionKey, caret: number) => void;
  /** Handles keyboard events for command navigation and selection. */
  handleKeyDown: (
    sectionKey: SectionKey,
    event: React.KeyboardEvent,
    currentValue: string
  ) => { handled: boolean; commitText?: string; caret?: number };
  /** Closes the command menu. */
  close: () => void;
  /** Returns the filtered list of commands based on current query. */
  getFilteredCommands: () => SlashCommand[];
}

/**
 * Hook for managing slash command menu state and interactions.
 * Handles opening/closing the menu, filtering commands by query, and processing keyboard input.
 *
 * @param options - Configuration options including available commands and insertion behavior.
 * @returns An object containing menu state and handler functions.
 */
export function useSlashCommands<SectionKey extends string = string>(
  options: UseSlashCommandsOptions<SectionKey>
): UseSlashCommandsReturn<SectionKey> {
  const { commands, getInsertionText } = options;

  const [state, setState] = useState<SlashState<SectionKey> | null>(null);

  const openForSection = useCallback(
    (sectionKey: SectionKey, caret: number) => {
      setState({
        isOpen: true,
        sectionKey,
        startOffset: caret,
        query: '',
        activeIndex: 0,
      });
    },
    []
  );

  const close = useCallback(() => {
    setState(null);
  }, []);

  const getFilteredCommands = useCallback((): SlashCommand[] => {
    if (!state) return [];
    return filterSlashCommands(commands, state.query);
  }, [commands, state]);

  useCloseRequest({
    isOpen: Boolean(state?.isOpen) && getFilteredCommands().length > 0,
    onClose: close,
  });

  const handleKeyDown = useCallback(
    (
      sectionKey: SectionKey,
      event: React.KeyboardEvent,
      currentValue: string
    ): { handled: boolean; commitText?: string; caret?: number } => {
      if (!state || !state.isOpen || state.sectionKey !== sectionKey) {
        return { handled: false };
      }

      const filtered = getFilteredCommands();
      const hasCommands = filtered.length > 0;

      const selectIndex = (): number => {
        if (!hasCommands) return -1;
        if (state.activeIndex >= 0 && state.activeIndex < filtered.length) {
          return state.activeIndex;
        }
        return 0;
      };

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return { handled: true };
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!hasCommands) {
          return { handled: false };
        }
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        setState((prev) => {
          if (!prev) return prev;
          const nextIndex =
            (prev.activeIndex + direction + filtered.length) % filtered.length;
          return { ...prev, activeIndex: nextIndex };
        });
        return { handled: true };
      }

      if (event.key === 'Enter') {
        if (!hasCommands) {
          close();
          return { handled: true, commitText: currentValue };
        }

        event.preventDefault();
        const index = selectIndex();
        const command = filtered[index];

        const result: InsertSlashCommandResult = insertSlashCommand({
          text: currentValue,
          startOffset: state.startOffset,
          query: state.query,
          command,
          getInsertionText,
        });

        close();
        return {
          handled: true,
          commitText: result.nextText,
          caret: result.caret,
        };
      }

      if (event.key === 'Backspace') {
        if (!state.query.length) {
          close();
          return { handled: false };
        }

        setState((prev) =>
          prev
            ? {
                ...prev,
                query: prev.query.slice(0, -1),
                activeIndex: 0,
              }
            : prev
        );
        return { handled: false };
      }

      if (
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        setState((prev) =>
          prev
            ? {
                ...prev,
                query: prev.query + event.key,
                activeIndex: 0,
              }
            : prev
        );
      }

      return { handled: false };
    },
    [close, getFilteredCommands, getInsertionText, state]
  );

  return {
    state,
    openForSection,
    handleKeyDown,
    close,
    getFilteredCommands,
  };
}
